import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Marquee } from '@sorare/core/src/atoms/container/Marquee';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LandingTitle } from 'components/landing/ui/LandingTitle';
import { Tag } from 'components/landing/ui/Tag';
import { Props } from 'pages/bestofeurope/types';

import backgroundAvif from './assets/background.avif';
import background from './assets/background.jpg';
import desktopAvif from './assets/desktop.avif';
import desktop from './assets/desktop.png';
import mobileAvif from './assets/mobile.avif';
import mobile from './assets/mobile.png';
import ribon from './assets/ribon.jpg';
import ribonBack from './assets/ribon_back.jpg';

const Root = styled.section`
  position: relative;
  isolation: isolate;
`;
const Grid = styled.div`
  position: relative;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  background-image: url(${background}});
  background-image: image-set(
    url(${backgroundAvif}) type('image/avif'),
    url(${background})
  );
  background-size: cover;
  background-position: center;
  padding: calc(10 * var(--unit)) var(--double-unit);
  &:before {
    position: absolute;
    pointer-events: none;
    content: '';
    inset: 0;
    z-index: 1;
  }
  @media ${laptopAndAbove} {
    display: grid;
    justify-content: center;
    gap: calc(10 * var(--unit));
    grid-template-columns: minmax(0, 725px) 536px;
  }
`;
const Content = styled(Vertical)`
  align-items: center;
  z-index: 1;
  text-align: center;
  @media ${laptopAndAbove} {
    align-items: flex-start;

    text-align: left;
  }
`;
const Ribbon = styled.div`
  position: relative;
  z-index: 2;
  transform: rotate(-0.7deg);
  &::before,
  &::after {
    position: absolute;
    inset: 0;
    z-index: -1;
    content: '';
  }
  &:before {
    transform: rotate(-1deg);
    background-image: url(${ribonBack});
  }
  &:after {
    background-image: url(${ribon});
  }
`;
const RibbonContent = styled.div`
  display: flex;
  align-items: center;
  font-size: 17px;
  text-transform: uppercase;
  height: 38px;
  @media ${laptopAndAbove} {
    height: 65px;
    font-size: 30px;
  }
`;

const TitleFormatted = styled(LandingTitle)`
  font-size: clamp(43px, 4.8vw, 72px);
`;

export const Needsyou = ({ signUp }: Props) => {
  return (
    <Root>
      <Ribbon>
        <Marquee direction="reverse" duration="20s">
          <RibbonContent>
            <FormattedMessage
              id="BestOfEurope.NeedsYou.marquee"
              defaultMessage="Next up: Submit your team to fly with AC Milan"
            />
          </RibbonContent>
        </Marquee>
      </Ribbon>
      <Grid>
        <picture>
          <source
            srcSet={desktopAvif}
            media={laptopAndAbove}
            type="image/avif"
            width="712"
            height="675"
          />
          <source
            srcSet={desktop}
            media={laptopAndAbove}
            width="712"
            height="675"
          />
          <source
            srcSet={mobileAvif}
            type="image/avif"
            width="544"
            height="675"
          />
          <Img src={mobile} fetchpriority="low" width="544" height="675" />
        </picture>
        <Content gap={3}>
          <Tag>
            <Horizontal>
              <FormattedMessage
                id="BestOfEurope.NeedsYou.chip"
                defaultMessage="Free to play"
              />
            </Horizontal>
          </Tag>
          <TitleFormatted className="left">
            <FormattedMessage
              id="BestOfEurope.NeedsYou.title"
              defaultMessage="Fantasy Football Reinvented"
            />
          </TitleFormatted>
          <BodyL>
            <FormattedMessage
              id="BestOfEurope.NeedsYou.desc"
              defaultMessage="Build teams featuring the best players in Europe. Play for once in a lifetime experiences, match tickets, jerseys and more twice a week every week."
            />
          </BodyL>
          <Button color="secondary" size="large" onClick={signUp}>
            <FormattedMessage {...glossary.playNow} />
          </Button>
        </Content>
      </Grid>
    </Root>
  );
};
